import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core'
import { ControlValueAccessor, UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR } from '@angular/forms'
import { BrowserCompatibilityService } from '../../../../../../services/browser-compatibility.service'
import { OverlayPanel } from 'primeng/overlaypanel'
import { Observable } from 'rxjs'
import { distinctUntilChanged, map, skip } from 'rxjs/operators'
import DateTimeUtils from '../../../../../../utils/date-time.utils'
import { isEqual } from 'lodash'

@Component({
  selector: 'jml-lib-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateFilterComponent),
      multi: true
    }
  ]
})
export class DateFilterComponent implements OnInit, ControlValueAccessor {
  @Input() header

  @ViewChild('op') op: OverlayPanel

  dateFilterOptions = [
    { label: 'Ist', value: 'IS' },
    { label: 'Bis', value: 'BEFORE' },
    { label: 'Ab', value: 'AFTER' },
    { label: 'Zwischen', value: 'BETWEEN' }
  ]
  isActive = false
  filterForm = new UntypedFormGroup({
    matchMode: new UntypedFormControl('IS'),
    dateSingle: new UntypedFormControl(undefined),
    dateRange: new UntypedFormControl(undefined)
  })
  onTouched = (obj) => {}
  onChange = (obj) => {}
  filterRepr$: Observable<string>
  showDialog = false

  constructor(private browserCompatibilityService: BrowserCompatibilityService) {}

  writeValue(obj: any): void {
    // console.log('write value', obj, this.filterForm);
    if (!!obj) {
      this.filterForm.get('matchMode').patchValue(obj.matchMode)
      this.filterForm.get('dateSingle').patchValue(DateTimeUtils.convertApiDateTimeStringToDate(obj.dateSingle))
      if (!!obj.dateRange) {
        this.filterForm
          .get('dateRange')
          .patchValue([
            DateTimeUtils.convertApiDateTimeStringToDate(obj.dateRange[0]),
            DateTimeUtils.convertApiDateTimeStringToDate(obj.dateRange[1])
          ])
      }
      this.isActive = true
    } else {
      this.filterForm.reset()
      this.isActive = false
    }
    this.filterForm.markAllAsTouched()
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  ngOnInit(): void {
    this.filterRepr$ = this.filterForm.valueChanges.pipe(
      // tap(v => console.log('filter repr', v, this.filterForm)),
      map(() => {
        // Build filter representation string
        const matchMode = this.filterForm.get('matchMode').value
        const dateSingleValue = this.filterForm.get('dateSingle').value
        const dateRangeValue = this.filterForm.get('dateRange').value
        let filterRepr = ''

        if (!!dateSingleValue && matchMode !== 'BETWEEN') {
          if (matchMode === 'IS' || matchMode === null) {
            filterRepr = 'ist ' + DateTimeUtils.convertDateToApiConformDateOnlyString(dateSingleValue)
          } else if (matchMode === 'BEFORE') {
            filterRepr = 'bis ' + DateTimeUtils.convertDateToApiConformDateOnlyString(dateSingleValue)
          } else if (matchMode === 'AFTER') {
            filterRepr = 'ab ' + DateTimeUtils.convertDateToApiConformDateOnlyString(dateSingleValue)
          }
        } else if (!!dateRangeValue && matchMode === 'BETWEEN' && !!dateRangeValue[0] && !!dateRangeValue[1]) {
          filterRepr = `${DateTimeUtils.convertDateToApiConformDateOnlyString(dateRangeValue[0])} - ${DateTimeUtils.convertDateToApiConformDateOnlyString(dateRangeValue[1])}`
        }
        return filterRepr
      })
    )
    this.filterForm.valueChanges
      .pipe(
        skip(3), // On initialization each control will emit once for the initial value. Since no actual change has happened dont let control emit
        distinctUntilChanged((prev, curr) => isEqual(prev, curr))
      )
      .subscribe((obj) => {
        if (!obj || (!obj.dateSingle && !obj.dateRange)) {
          this.onChange(null)
          this.onTouched(null)
          return
        }
        this.onChange(obj)
        this.onTouched(obj)
      })
  }

  onIconClick(event) {
    if (this.browserCompatibilityService.isSmallScreen() || this.browserCompatibilityService.isCoarsePointer()) {
      this.showDialog = true
    } else {
      this.op.toggle(event)
    }
  }

  resetFilter() {
    this.op.hide()
    this.showDialog = false
    this.filterForm.reset()
    this.filterForm.markAllAsTouched()
    this.isActive = false
  }

  onDateInput(value, isRange: boolean) {
    console.log('on date input', value, isRange)
    if (isRange) {
      this.filterForm.patchValue({ dateRange: value })
    } else {
      this.filterForm.patchValue({ dateSingle: value })
    }
    this.updateActiveStatus()
  }

  onDateSelect(isRange: boolean) {
    if (!isRange || (isRange && !!this.filterForm.get('dateRange').value[1])) {
      this.op.hide()
      this.showDialog = false
    }
    this.updateActiveStatus()
  }

  updateActiveStatus() {
    const matchMode = this.filterForm.get('matchMode').value
    const dateSingleValue = this.filterForm.get('dateSingle').value
    const dateRangeValue = this.filterForm.get('dateRange').value
    this.isActive =
      (!!dateSingleValue && matchMode !== 'BETWEEN') ||
      (!!dateRangeValue && matchMode === 'BETWEEN' && !!dateRangeValue[0] && !!dateRangeValue[1])
  }

  onKeydownEnter() {
    if (this.isActive) this.op.hide()
  }

  // Format current date to filter expression and emit through filter output
  public static formatFilterValue(matchMode, dateSingleValue, dateRangeValue): string {
    console.log('format filter value', matchMode, dateSingleValue, dateRangeValue)
    let formattedValue = undefined

    if (!!dateSingleValue && matchMode !== 'BETWEEN') {
      if (matchMode === 'IS' || matchMode === null) {
        formattedValue = `'${DateTimeUtils.convertApiDateTimeStringToCalendarString(dateSingleValue)}'`
      } else if (matchMode === 'BEFORE' && !!dateSingleValue) {
        formattedValue = `None, '${DateTimeUtils.convertApiDateTimeStringToCalendarString(dateSingleValue)}'`
      } else if (matchMode === 'AFTER' && !!dateSingleValue) {
        formattedValue = `'${DateTimeUtils.convertApiDateTimeStringToCalendarString(dateSingleValue)}', None`
      }
    } else if (!!dateRangeValue && matchMode === 'BETWEEN' && !!dateRangeValue[0] && !!dateRangeValue[1]) {
      formattedValue = `'${DateTimeUtils.convertApiDateTimeStringToCalendarString(dateRangeValue[0])}', '${DateTimeUtils.convertApiDateTimeStringToCalendarString(dateRangeValue[1])}'`
    }
    return formattedValue
  }
}
